/**
 * The offline edit queue's decisions (the editor session, `api/rundownEditor.ts`,
 * does the I/O): which failed sends are held and retried rather than rolled
 * back, how long to wait between retries, and how much may be held.
 *
 * **Why replay is safe** (PLAN §6.1, D6): a held patch keeps its row id, HLC
 * and clientOpId, and the merge is per-field LWW on the HLC — idempotent,
 * so a batch the server applied before the answer was lost is a no-op the
 * second time, and commutative, so a newer write by someone else during the
 * outage still wins its field, exactly as if the patch had arrived on time.
 * A script's Yjs update is idempotent too. What the server refuses on replay
 * (a nack, a 4xx) is rolled back and reported per edit, as it is online.
 */
import { ApiError } from '@norna/api-client/client.js';
import { isUnreachable } from '../connectivity/failover.js';

/** At most this many unanswered patches while offline; an edit past it is refused (not applied) and said so. */
export const MAX_OFFLINE_PATCHES = 1_000;

/** The first retry after a failed send, doubling per failure up to `RETRY_MAX_MS`. */
export const RETRY_MIN_MS = 1_000;
export const RETRY_MAX_MS = 30_000;

/**
 * Whether a failed send means "try again later" rather than "no": the
 * backend did not answer (`isUnreachable`: the fetch failed, or a gateway's
 * bare 502/503/504), or it answered that it cannot take the write now
 * (503 `unavailable`, 429 rate limited). Anything else it answered — a
 * validation error, forbidden, not found, a 500 — is an answer, and replaying
 * it would only fail the same way forever.
 */
export function isTransientFailure(err: unknown): boolean {
  if (isUnreachable(err)) return true;
  return err instanceof ApiError && (err.status === 503 || err.status === 429);
}

/** The wait before retry number `failures` (1 = after the first failure). */
export function retryDelayMs(failures: number): number {
  const exponent = Math.max(0, Math.min(failures - 1, 16));
  return Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** exponent);
}

/** Whether `adding` more patches fit beside the `held` ones. Online nothing is held for long, so only offline counts. */
export function fitsInQueue(offline: boolean, held: number, adding: number): boolean {
  return !offline || held + adding <= MAX_OFFLINE_PATCHES;
}
